import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { CallGroupApi } from '@frontend/api-call-group';
import { ForwardingNumberApi } from '@frontend/api-forwarding-number';
import SipProfileAPI from '@frontend/api-sip-profile';
import { scopedFilterAndSort } from '@frontend/filters';
import { Trans, useTranslation } from '@frontend/i18n';
import { usePhoneLocationFilterShallowStore, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useScopedQuery } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { Text } from '@frontend/design-system';
import { CreateCallGroupButton } from '../../components/call-group/create-button';
import { DeleteCallGroupIconButton } from '../../components/call-group/delete-button';
import { IndexTable } from '../../components/common/index-table';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { queryKeys } from '../../query-keys';
import { usePersistentPagination } from './usePersistentPagination';

export function CallGroupIndex() {
  const { navigate } = useSettingsNavigate();
  const { locationFilters, setLocationFilters } = usePhoneLocationFilterShallowStore(
    'locationFilters',
    'setLocationFilters'
  );
  const { settingsTenantLocation, globalAvailableLocationIds } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'globalAvailableLocationIds'
  );
  const { t } = useTranslation('phone', { keyPrefix: 'call-groups' });
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const [_pagination, _setPagination, paginationTableProps, paginationConfig] =
    usePersistentPagination('call-groups-pagination');

  const filteredLocations = locationFilters.callGroupsIndex;
  const { data: callGroups = [], isLoading } = useScopedQuery({
    queryKey: queryKeys.listCallGroups(tenantId),
    queryFn: () => CallGroupApi.listCallGroups({ tenantId: tenantId }),
    select: (data) => data.callGroups ?? [],
    enabled: !!settingsTenantLocation?.phoneTenantId,
    staleTime: 5 * 60 * 1000,
    meta: {
      error: {
        message: 'CallGroupIndex:listCallGroupsQuery | Failed to fetch call groups',
        data: { tenantId },
      },
    },
  });

  const prefilteredCallGroups = useMemo(
    () =>
      scopedFilterAndSort({
        data: callGroups,
        scopeIds: globalAvailableLocationIds,
        filteredScopeIds: filteredLocations,
      }),
    [callGroups, globalAvailableLocationIds, filteredLocations]
  );

  const filteredCallGroups = useMemo(() => {
    return prefilteredCallGroups.filter(({ name }) => !name.includes('e911'));
  }, [prefilteredCallGroups]);

  const { data: devices } = useScopedQuery({
    queryKey: queryKeys.devicesWithAddresses(),
    queryFn: () => SipProfileAPI.List({ tenantId: tenantId }).then((res) => res.sipProfiles),
    select: (data) =>
      data?.sort((a, b) => {
        const aName = a.device?.deviceName || '';
        const bName = b.device?.deviceName || '';
        return aName.localeCompare(bName);
      }),
    enabled: !!tenantId,
    meta: {
      error: {
        message: 'CallGroupIndex:listDevicesQuery | Failed to fetch devices',
        data: { tenantId },
      },
    },
  });

  const callForwardingNumbers = useQuery({
    queryKey: [queryKeys.forwardingNumbers(), settingsTenantLocation?.id],
    queryFn: () => ForwardingNumberApi.list({ locationId: settingsTenantLocation?.id }),
    select: (data) =>
      data.sort((a, b) => {
        const aName = a.name || '';
        const bName = b.name || '';
        return aName.localeCompare(bName);
      }),
    enabled: !!settingsTenantLocation?.id,
    meta: {
      error: {
        message: 'CallGroupIndex:listForwardingNumbersQuery | Failed to fetch forwarding numbers',
        data: { locationId: settingsTenantLocation?.id },
      },
    },
  });

  return (
    <PhoneSettingsPage
      action={
        settingsTenantLocation ? (
          <CreateCallGroupButton
            tenantLocation={settingsTenantLocation}
            devices={devices ?? []}
            callForwardingNumbers={callForwardingNumbers.data ?? []}
          />
        ) : null
      }
      title={'Call Groups'}
      subtitle={t(
        "Assign devices to a group, have them ring simultaneously or in a cascading order, and set each device's ring duration."
      )}
      breadcrumbs={[
        { label: t('Phone'), to: '/phone/main' },
        { label: t('Call Groups'), to: '/phone/call-groups' },
      ]}
    >
      {settingsTenantLocation ? (
        <IndexTable
          {...paginationTableProps}
          labelsColumnPosition={2}
          tableConfig={{
            ...paginationConfig,
            colConfig: [
              {
                id: 'name',
                Header: t('Name'),
                accessor: (data) => data.name,
                sortType: 'string',
              },
              {
                id: 'count',
                Header: t('Devices'),
                accessor: (data) => data.devicesCount,
                cellRenderer: (value) => <Text>{t('{{count}} devices', { count: value })}</Text>,
                sortType: 'number',
                width: 100,
              },
            ],
            data: filteredCallGroups ?? [],
            isLoading,
            emptyStateConfig: {
              type: 'sync_your_phone',
              header: t('No Call Groups'),
              description: () =>
                !!filteredCallGroups?.length ? (
                  <Text textAlign='center'>{t('There are no Call Groups to display with this filter setting.')}</Text>
                ) : (
                  <Trans t={t}>
                    <Text textAlign='center'>You don't have any call groups yet. </Text>
                    <Text textAlign='center'>
                      Click{' '}
                      <Text as='span' weight='bold'>
                        Create Call Group
                      </Text>{' '}
                      to get started.
                    </Text>
                  </Trans>
                ),
            },
          }}
          tenantLocation={settingsTenantLocation}
          idAccessor={(data) => data.id}
          nameAccessor={(data) => data.name}
          labelsAccessor={(data) => data.locationIds}
          setFilteredLocations={(locations) => {
            setLocationFilters({ callGroupsIndex: locations });
          }}
          filteredLocations={filteredLocations}
          DeleteIconButton={DeleteCallGroupIconButton}
          onRowClick={(row) => {
            navigate({ to: '/phone/call-groups/:id', params: { id: row.id } });
          }}
        />
      ) : (
        <div>{t('No Phone Tenant Selected')}</div>
      )}
    </PhoneSettingsPage>
  );
}
